import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { NotificationService } from './notification.service';

@Injectable()
export class DataIOErrorInterceptor implements HttpInterceptor {
  private notificationService = inject(NotificationService);

  private deidProfileRegEx = /\/settings\/deid_profile$/;
  private allowedUrls = [
    /^\/xfer\/imports\/[a-zA-Z0-9]{24}$/,
    /^xfer\/imports\/[a-zA-Z0-9]{24}\/progress2$/,
    /\/xfer\/blob-sessions$/,
    /^\/xfer\/blob-sessions\/[a-zA-Z0-9]{24}\/finish$/,
    /^xfer\/blob-sessions\/[a-zA-Z0-9]{24}$/,
    /^\/xfer\/conflicts\/resolve-methods$/,
    /^\/xfer\/conflicts\/resolve$/,
    /^\/xfer\/blobs$/,
    /^\/xfer\/blobs\/[a-zA-Z0-9]{24}\/upload$/,
    /^\/xfer\/blobs\/[a-zA-Z0-9]{24}\/finish$/,
    /^\/xfer\/blobs\/[a-zA-Z0-9]{24}$/,
    /^\/xfer\/exports\/[a-zA-Z0-9]{24}\/report$/,
    /^\/xfer\/exports\/[a-zA-Z0-9]{24}\/progress2$/,
    /^\/snapshot\/projects\/[a-zA-Z0-9]{24}\/snapshots\/[a-fA-F0-9]{24}\/detail$/,
    /^\/snapshot\/projects\/[a-zA-Z0-9]{24}\/snapshots$/,
    this.deidProfileRegEx,
  ];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const url = error.url;
        const status = error.status;
        const defaultValue = throwError(() => error);

        if (!this.allowedUrls.some((allowedUrl) => allowedUrl.test(error.url))) {
          return defaultValue;
        }

        if (this.deidProfileRegEx.test(url) && status === 404) {
          return defaultValue;
        }

        const message = error.error?.message ?? error.error?.detail;

        if (message) {
          this.notificationService.error(message, true);
        } else {
          switch (status) {
            case 400:
              this.notificationService.error('common.badRequest');
              break;
            case 401:
              this.notificationService.error('common.unauthorized');
              break;
            case 403:
              this.notificationService.error('common.forbidden');
              break;
            case 404:
              this.notificationService.error('common.notFound');
              break;
            case 409:
              this.notificationService.error('common.conflict');
              break;
            case 422:
              this.notificationService.error('common.unprocessable');
              break;
            case 429:
              this.notificationService.error('common.tooManyRequests');
              break;
            case 500:
              this.notificationService.error('common.serverError');
              break;
            case 503:
              this.notificationService.error('common.serviceUnavailable');
              break;
            default:
              this.notificationService.error('common.somethingWentWrong');
              break;
          }
        }

        return throwError(() => error);
      }),
    );
  }
}
